import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { formatEatDate, formatEatTime } from '@wodi/domain';
import { CHECK_IN_VISIT_TYPES, type ServicePointView, type VisitView } from '@wodi/shared';
import { Button, Kbd, LoadingBlock, Notice, RadioGroup, Section, useShortcut } from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { patientKeys } from '../api/patients.ts';
import { shaKeys } from '../api/sha.ts';
import { openVisit, servicePointsQuery, visitQuery } from '../api/visits.ts';
import { InsurerCoverChoice } from '../insurance/cover.tsx';
import { insuranceKeys } from '../api/insurance.ts';
import { ShaCover, type ShaSelection } from '../sha/sha-cover.tsx';
import { useOnline } from '../hooks/use-online.ts';
import { VISIT_TYPE_LABELS } from './patient-banner.tsx';

type CheckInVisitType = (typeof CHECK_IN_VISIT_TYPES)[number];
import styles from './patients.module.css';

/** Triage by default (plan §6.3); otherwise the first in the facility's order. */
export function defaultServicePoint(points: readonly ServicePointView[]): string | null {
  return (points.find((p) => p.kind === 'triage') ?? points[0])?.id ?? null;
}

function AlreadyOpen({ visitId, patientId }: { visitId: string; patientId: string }) {
  const visit = useQuery(visitQuery(visitId));
  const when =
    visit.data === undefined
      ? ''
      : `, started ${formatEatDate(new Date(visit.data.startedAt))} ${formatEatTime(new Date(visit.data.startedAt))}`;
  const type =
    visit.data === undefined ? '' : `${VISIT_TYPE_LABELS[visit.data.type].toLowerCase()} `;
  return (
    <Notice
      tone="info"
      action={
        <Link to="/patients/$patientId" params={{ patientId }}>
          Open visit
        </Link>
      }
    >
      {`This patient already has an open ${type}visit${when}.`}
    </Notice>
  );
}

/**
 * The check-in panel (plan §6.3): visit type, payer and first queue, then "Check in"
 * (Ctrl/⌘Enter). Opening a visit creates its first queue entry in the same request.
 * The request ID is made once per panel, so a retried check-in can't open two visits.
 */
export function CheckInPanel({
  patientId,
  onCheckedIn,
  canCheckSha = false,
  appointment,
}: {
  patientId: string;
  onCheckedIn: (visit: VisitView) => void;
  /**
   * Checking in for a booked appointment (docs/plans/phase2-appointments.md §6): the visit
   * marks it arrived, and its clinic is the first queue unless changed.
   */
  appointment?: { id: string; servicePointId: string };
  /** The person may check SHA cover (`sha.eligibility.check`), so SHA is a payer choice. */
  canCheckSha?: boolean;
}) {
  const online = useOnline();
  const queryClient = useQueryClient();
  const points = useQuery(servicePointsQuery);
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [type, setType] = useState<CheckInVisitType>('outpatient');
  const [chosenPoint, setChosenPoint] = useState<string | null>(null);
  const [payerKind, setPayerKind] = useState<'cash' | 'sha' | 'insurer'>('cash');
  const [shaSelection, setShaSelection] = useState<ShaSelection | null>(null);
  // A private insurer's cover recorded for the patient (private insurance plan §6 item 2).
  const [coverageId, setCoverageId] = useState<string | null>(null);
  // A new SHA check each time SHA is chosen again (docs/plans/phase1-sha.md §9.1).
  const [shaRound, setShaRound] = useState(0);
  const activePoints = (points.data ?? []).filter((p) => p.status === 'active');
  const appointmentPoint = activePoints.find((p) => p.id === appointment?.servicePointId);
  const servicePointId = chosenPoint ?? appointmentPoint?.id ?? defaultServicePoint(activePoints);
  // A casualty queue opens an emergency visit, and only it does (casualty plan §1.1).
  const atCasualty = activePoints.find((p) => p.id === servicePointId)?.kind === 'casualty';
  const visitType: CheckInVisitType = atCasualty ? 'emergency' : type;

  const checkIn = useMutation({
    mutationFn: (firstServicePointId: string) =>
      openVisit({
        clientRequestId,
        patientId,
        type: visitType,
        payer:
          payerKind === 'sha' && shaSelection !== null
            ? { kind: 'sha', ...shaSelection }
            : payerKind === 'insurer' && coverageId !== null
              ? { kind: 'insurer', coverageId }
              : { kind: 'cash' },
        firstServicePointId,
        ...(appointment === undefined ? {} : { appointmentId: appointment.id }),
      }),
    onSuccess: async (visit) => {
      await queryClient.invalidateQueries({ queryKey: patientKeys.banner(patientId) });
      await queryClient.invalidateQueries({ queryKey: patientKeys.visits(patientId) });
      await queryClient.invalidateQueries({ queryKey: shaKeys.checks(patientId) });
      await queryClient.invalidateQueries({ queryKey: ['appointments'] });
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.covers(patientId) });
      onCheckedIn(visit);
    },
  });

  const payerReady =
    payerKind === 'cash' ||
    (payerKind === 'sha' && shaSelection !== null) ||
    (payerKind === 'insurer' && coverageId !== null);
  const canSubmit = online && servicePointId !== null && payerReady && !checkIn.isPending;
  const submit = () => {
    if (canSubmit) checkIn.mutate(servicePointId);
  };
  useShortcut('mod+enter', submit, { allowInFields: true, description: 'Check in' });

  const error = checkIn.error;
  const openVisitId =
    error instanceof ApiError &&
    error.code === 'visit_already_open' &&
    typeof error.context?.['visitId'] === 'string'
      ? error.context['visitId']
      : null;

  return (
    <Section title="Check in">
      {points.isPending ? <LoadingBlock label="Loading queues" rows={2} /> : null}
      {points.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void points.refetch()}>
              Try again
            </Button>
          }
        >
          {points.error instanceof ApiError
            ? points.error.message
            : "The queues couldn't be loaded. Try again."}
        </Notice>
      ) : null}
      {points.isSuccess && activePoints.length === 0 ? (
        <Notice tone="info">
          No queues are set up at this facility, so patients can't be checked in yet. An
          administrator can add them in Settings, under Service points.
        </Notice>
      ) : null}
      {points.isSuccess && activePoints.length > 0 ? (
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          {/* Visit type, payer and first queue side by side where there is room (SHA cover
              takes a row of its own): with the defaults the whole panel fits above the fold
              at 1366 × 768, so most check-ins are a single press of Check in (Ctrl Enter). */}
          <div className={styles.checkInGrid}>
            {atCasualty ? (
              <div className={styles.readOnly}>
                <span className={styles.readOnlyLabel}>Visit type</span>
                <span>{VISIT_TYPE_LABELS.emergency}</span>
              </div>
            ) : (
              <RadioGroup
                label="Visit type"
                orientation="horizontal"
                value={type}
                onChange={(value) => {
                  setType(value as CheckInVisitType);
                }}
                options={CHECK_IN_VISIT_TYPES.filter((t) => t !== 'emergency').map((t) => ({
                  id: t,
                  label: VISIT_TYPE_LABELS[t],
                }))}
              />
            )}
            <RadioGroup
              label="Payer"
              orientation="horizontal"
              value={payerKind}
              onChange={(value) => {
                const next = value === 'sha' ? 'sha' : value === 'insurer' ? 'insurer' : 'cash';
                setPayerKind(next);
                setShaSelection(null);
                setCoverageId(null);
                if (next === 'sha') setShaRound((n) => n + 1);
              }}
              options={[
                { id: 'cash', label: 'Cash' },
                ...(canCheckSha ? [{ id: 'sha', label: 'SHA' }] : []),
                { id: 'insurer', label: 'Insurance' },
              ]}
            />
            {payerKind === 'insurer' ? (
              <div className={styles.checkInWide}>
                <InsurerCoverChoice
                  patientId={patientId}
                  coverageId={coverageId}
                  onSelect={setCoverageId}
                />
              </div>
            ) : null}
            {payerKind === 'sha' ? (
              <div className={styles.checkInWide}>
                <ShaCover
                  key={shaRound}
                  patientId={patientId}
                  onSelect={setShaSelection}
                  onUseCash={() => {
                    setPayerKind('cash');
                    setShaSelection(null);
                  }}
                />
              </div>
            ) : null}
            <RadioGroup
              label="First queue"
              orientation="horizontal"
              value={servicePointId ?? ''}
              onChange={setChosenPoint}
              options={activePoints.map((p) => ({ id: p.id, label: p.name }))}
            />
          </div>
          {openVisitId !== null ? (
            <AlreadyOpen visitId={openVisitId} patientId={patientId} />
          ) : error !== null ? (
            <Notice tone="error">
              {error instanceof ApiError
                ? error.message
                : "The patient couldn't be checked in. Try again."}
            </Notice>
          ) : null}
          {online ? null : (
            <Notice tone="info">
              Checking in needs a connection to Wodi. Reconnect, then check the patient in.
            </Notice>
          )}
          <div className={styles.actions}>
            <Button
              type="submit"
              variant="primary"
              isDisabled={!online || servicePointId === null || !payerReady}
              isPending={checkIn.isPending}
              pendingLabel="Checking in…"
            >
              Check in
            </Button>
            <Kbd combo="mod+enter" />
          </div>
        </form>
      ) : null}
    </Section>
  );
}
